import {
  Box,
  Button,
  Divider,
  Grid,
  Link,
  Paper,
  TextField,
  Typography,
} from 'app/design';
import {
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
} from 'app/design/icons-material';
import { QuickFinderUserDialog } from 'app/components/QuickFinderUserDialog';
import { useUserQuery } from 'app/hooks/queries/user';
import { UserCallerIdCard } from 'app/pages/admin/Users/View/components/UserCallerIdCard';
import { UserSettingsCard } from 'app/pages/admin/Users/View/components/UserSettingsCard';
import { getAtPath, setAtPath, useToggleReducer } from 'app/utilities';
import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import { NodeEditDialog } from '../../IvrBuilder/Dialogs';
import { NodeInfoDisplay } from '../../IvrBuilder/Flow/nodes/common/NodeInfoDisplay';
import { Link as RouterLink } from 'react-router-dom';

// import Select from 'react-select';

// Select users from list of users
export const User = props => {
  const {
    skipEditing,
    modifyPath,
    callflow,
    setCallflow,
    variant,
    showModifyDialog,
    setShowModifyDialog,
  } = props;

  const dataModifyPath = `${modifyPath}.data`;

  const moduleItem = getAtPath(callflow, modifyPath, {}); // { ring_type: 'custom', data: {...} }
  const ringItem = moduleItem?.data;

  // console.log('moduleItem:', moduleItem);
  /*
  moduleItem = {
    type: 'RingStrategy',
    data: {
      ring_type: 'user',
      data: {
        userId: 'xyz',
        timeout: 10
      }
    }
  }
  */

  const {
    data: user,
    isLoading: userIsLoading,
    error: userError,
    refetch: refetchUser,
  } = useUserQuery(ringItem?.data?.id);

  // console.log('ring user:', user);

  const [isModifying, setIsModifying] = useState(null);

  useEffect(() => {
    if (setShowModifyDialog) {
      // parent is managing "display" state, NOT this component
      // setShowModifyDialog()
      setIsModifying(showModifyDialog);
    }
  }, [showModifyDialog]);

  const handleUpdate = compData => {
    setAtPath(callflow, `${dataModifyPath}`, compData);
    setCallflow({ ...callflow });
  };

  const handleShowModifying = value =>
    setShowModifyDialog ? setShowModifyDialog(value) : setIsModifying(value);

  if (variant === 'ivr_builder') {
    return (
      <>
        {isModifying && (
          <ModifyRingSettings
            ringInfo={ringItem}
            onChangeRingInfo={handleUpdate}
            onClose={e => handleShowModifying(null)}
          />
        )}
        <NodeInfoDisplay
          type={'Ring User'}
          icon={<PersonIcon />}
          title={
            user ? (
              user.extra?.fullName
            ) : skipEditing ? (
              'None Selected'
            ) : (
              <Link onClick={() => handleShowModifying(true)}>Select User</Link>
            )
          }
          footer={
            ringItem?.data?.timeout &&
            user &&
            `${ringItem?.data?.timeout} seconds`
          }
        />
      </>
    );
  }

  return (
    <>
      {isModifying && (
        <ModifyRingSettings
          ringInfo={ringItem}
          onChangeRingInfo={handleUpdate}
          onClose={e => handleShowModifying(null)}
        />
      )}
      <Paper variant="outlined" elevation={0}>
        {user ? (
          <>
            <Typography variant="body1">
              Ringing <strong>{user.extra.fullName}</strong>
            </Typography>
            <Typography variant="subtitle1">
              {ringItem?.data?.timeout} seconds
            </Typography>
          </>
        ) : (
          <Typography variant="body1" style={{ fontStyle: 'italic' }}>
            No user chosen for ringing
          </Typography>
        )}
        <div>
          <Link onClick={e => handleShowModifying(true)}>Modify</Link>
        </div>
      </Paper>
    </>
  );
};

const timeoutDefault = 20;

const ModifyRingSettings = props => {
  const { ringInfo, onChangeRingInfo, onClose } = props;

  const [modifyData, setModifyData] = useState(() => cloneDeep(ringInfo));

  const userId = modifyData?.data?.id;

  const {
    data: user,
    isLoading: userIsLoading,
    error: userError,
    refetch: refetchUser,
  } = useUserQuery(userId);

  // defaults
  useEffect(() => {
    // timeout
    if (!getAtPath(modifyData, `data.timeout`)) {
      setAtPath(modifyData, `data.timeout`, timeoutDefault);
      setModifyData({ ...modifyData });
    }
  }, [modifyData, setModifyData]);

  const [initialState] = useState({
    id: ringInfo?.data?.id,
    timeout: ringInfo?.data?.timeout ?? timeoutDefault,
  });

  const saveLabel = !userId
    ? 'Leave Empty'
    : !initialState.id
    ? 'Add'
    : initialState.id === userId &&
      Number(initialState.timeout) === Number(modifyData?.data?.timeout)
    ? 'Done'
    : 'Update';

  // Finder Dialog
  const [showFinderDialog, toggleShowFindUserDialog] = useToggleReducer(
    !userId,
  );

  const onChangeUserSelect = selected => {
    const [userId] = selected;
    setAtPath(modifyData, `data.id`, userId); // newValue?.value === id
    setModifyData({ ...modifyData });
    toggleShowFindUserDialog();
  };

  const handleClear = () => {
    setAtPath(modifyData, `data.id`, null); // newValue?.value === id
    setModifyData({ ...modifyData });
    toggleShowFindUserDialog();
  };

  const [showAllSettings, toggleShowAllSettings] = useToggleReducer(false);

  return (
    <>
      <NodeEditDialog
        title={'Edit User Ringing'}
        maxWidth={showAllSettings ? 'md' : 'sm'}
        onClose={onClose}
        onCancel={onClose}
        onSave={() => {
          onChangeRingInfo(modifyData);
          onClose();
        }}
        titleButton={
          user ? (
            <Button color="primary" onClick={toggleShowFindUserDialog}>
              Change User
            </Button>
          ) : null
        }
        saveLabel={saveLabel}
        hideCancel={saveLabel === 'Done'}
        isLoading={userIsLoading}
        errorMessage={userError ? 'Failed loading selected user' : undefined}
      >
        {user ? (
          <Grid container spacing={3}>
            <Grid item xs={7}>
              <Typography variant={'h4'}>User Settings</Typography>
              <Divider />
              <Grid container justifyContent={'space-between'}>
                <Grid item>
                  <UserSettings
                    {...props}
                    modifyData={modifyData}
                    setModifyData={setModifyData}
                    user={user}
                  />
                </Grid>
                {showAllSettings ? (
                  <Grid item>
                    <Button onClick={toggleShowAllSettings}>
                      <ExpandLessIcon />
                    </Button>
                  </Grid>
                ) : null}
              </Grid>
              {showAllSettings ? (
                <>
                  <Box sx={{ paddingX: 2 }}>
                    <UserSettingsCard user={user} onComplete={refetchUser} />
                    <br />
                    <UserCallerIdCard user={user} onComplete={refetchUser} />
                  </Box>
                  <br />
                  <Divider />
                </>
              ) : null}
              <br />
              <Link underline={'hover'} onClick={toggleShowAllSettings}>
                {showAllSettings
                  ? 'Hide additional settings'
                  : 'Show all settings'}
              </Link>
            </Grid>
            <Grid item xs={5}>
              <Typography variant={'h4'}>Ring Settings</Typography>
              <Divider />
              <RingSettings
                {...props}
                modifyData={modifyData}
                setModifyData={setModifyData}
              />
            </Grid>
          </Grid>
        ) : (
          <>
            <Typography color={'gray'} sx={{ fontStyle: 'italic' }}>
              No User Selected...
            </Typography>
            <Button onClick={toggleShowFindUserDialog}>Choose User</Button>
          </>
        )}
      </NodeEditDialog>
      {showFinderDialog ? (
        <QuickFinderUserDialog
          onSelect={onChangeUserSelect}
          onCancel={toggleShowFindUserDialog}
          onClear={handleClear}
          initialSelected={userId ? [userId] : []}
          selectionTitle={user ? user.extra.fullName : null}
        />
      ) : null}
    </>
  );
};

const UserSettings = ({ modifyData, setModifyData, user }) => {
  const userId = modifyData?.data?.id;
  console.log('UserId:', userId, modifyData);

  return (
    <Box sx={{ paddingTop: 1, paddingBottom: 1 }}>
      <Link
        variant="h1"
        to={`/admin/users/view/${userId}`}
        underline="hover"
        color={'text.primary'}
        component={RouterLink}
      >{`${user.extra.fullName}`}</Link>
    </Box>
  );
};

const RingSettings = ({ modifyData, setModifyData }) => {
  const handleChangeText = e => {
    setAtPath(modifyData, `data.timeout`, e.target.value); // newValue?.value === id
    setModifyData({ ...modifyData });
  };
  return (
    <Box sx={{ marginTop: 2 }}>
      <div>
        <TextField
          label="Ring Duration (seconds)"
          value={modifyData?.data?.timeout}
          onChange={handleChangeText}
          type="number"
          inputProps={{
            min: 0,
          }}
          size="small"
        />
      </div>
    </Box>
  );
};

export default User;
